import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { authService } from '@/services/auth.service';
import apiClient from '@/services/api';
import { devLog } from '@/utils/logger';
import { useAuth } from '@/hooks/useAuth';
import type { PurchaseSummaryItem, PurchaseSummaryResponse } from '@/types/purchase';

/**
 * WelcomePage — Post-purchase landing page (public, no auth required)
 *
 * Query params:
 *   email  — customer email address (optional)
 *   order  — WooCommerce order ID, pass-through only (ignored in UI)
 *   name   — customer first name (optional, reserved for future use)
 *
 * States:
 *   no-email   → generic "check your email" fallback
 *   polling    → provisioned=false → spinner + keep polling every 4 s
 *   ready      → provisioned=true  → success card + login CTA + resend link
 */

// ─── API shape ───────────────────────────────────────────────────────────────

interface PurchaseStatusData {
  provisioned: boolean;
  accountType: 'team' | 'agency' | null;
  needsPasswordSetup: boolean;
}

interface PurchaseStatusResponse {
  success: boolean;
  data: PurchaseStatusData;
}

// ─── Resend throttle (ms) — 30 s after first click ───────────────────────────
const RESEND_COOLDOWN_MS = 30_000;

// ─── Component ───────────────────────────────────────────────────────────────

export const WelcomePage = () => {
  const { t } = useTranslation('common');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const email = searchParams.get('email') ?? '';
  const order = searchParams.get('order') ?? '';
  // name is available but not currently used in UI
  // const name = searchParams.get('name') ?? '';

  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const resendCooldownRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Latch: ensure we only redirect once even if the effect re-evaluates
  const redirectedRef = useRef(false);

  // Clean up cooldown timer on unmount
  useEffect(() => {
    return () => {
      if (resendCooldownRef.current) {
        clearTimeout(resendCooldownRef.current);
      }
    };
  }, []);

  // ─── Poll purchase status ─────────────────────────────────────────────────
  const {
    data: statusData,
    isError: statusError,
  } = useQuery<PurchaseStatusData>({
    queryKey: ['purchase-status', email],
    queryFn: async (): Promise<PurchaseStatusData> => {
      const response = await apiClient.get<PurchaseStatusResponse>(
        `/api/onboarding/purchase-status`,
        { params: { email } }
      );
      return response.data.data;
    },
    // Only poll when we have an email and haven't yet provisioned
    enabled: !!email,
    // Poll every 4 s; stop once provisioned=true
    refetchInterval: (query) => {
      if (query.state.data?.provisioned) return false;
      return 4_000;
    },
    // Never throw to the user — errors fall back to generic state
    retry: false,
    // Keep stale data visible while re-fetching
    staleTime: 0,
  });

  // ─── Redirect logged-in buyer straight to Settings → Billing ────────────
  // Conditions (ALL must be true):
  //   • isAuthenticated   — valid session in this browser
  //   • email match       — session email == purchase email (case-insensitive)
  //   • provisioned       — workspace is ready (don't skip the spinner)
  //
  // If email does NOT match (different account logged in), fall through to
  // the public welcome UI so the buyer can log in with their own credentials.
  //
  // `redirectedRef` latches the effect so it fires at most once per mount,
  // and `replace: true` removes this page from history (no back-loop).
  useEffect(() => {
    if (redirectedRef.current) return;
    if (!isAuthenticated) return;
    if (!statusData?.provisioned) return;

    const sessionEmail = user?.email?.trim().toLowerCase() ?? '';
    const purchaseEmail = email.trim().toLowerCase();

    if (!purchaseEmail || sessionEmail !== purchaseEmail) return;

    // Latch immediately to prevent double-fire on any re-evaluation
    redirectedRef.current = true;

    // Failsafe-fetch purchase summary; a timeout or error must never block the
    // redirect — we fall back to an empty items list and the generic toast fires.
    const SUMMARY_TIMEOUT_MS = 2500;

    const fetchSummary = async (): Promise<PurchaseSummaryItem[]> => {
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('summary_timeout')), SUMMARY_TIMEOUT_MS)
      );

      const fetchPromise = apiClient.get<PurchaseSummaryResponse>(
        '/api/onboarding/purchase-summary',
        { params: { email, order } }
      ).then((res) => res.data.data.items);

      return Promise.race([fetchPromise, timeoutPromise]);
    };

    fetchSummary()
      .catch((err: unknown) => {
        devLog.warn('[WelcomePage] purchase-summary fetch failed (non-blocking):', err);
        return [] as PurchaseSummaryItem[];
      })
      .then((items) => {
        const hasWhitelabel = items.some((i) => i.kind === 'whitelabel');
        const isAgency = statusData?.accountType === 'agency';
        const destination = isAgency
          ? hasWhitelabel
            ? '/agency/settings/whitelabel?purchase=success'
            : '/agency/settings?tab=billing&purchase=success'
          : hasWhitelabel
            ? '/settings/whitelabel?purchase=success'
            : '/settings?tab=billing&purchase=success';

        navigate(destination, {
          replace: true,
          state: { purchaseSummary: items },
        });
      });
  }, [isAuthenticated, user?.email, statusData?.provisioned, email, order, navigate]);

  // ─── Resend login link ────────────────────────────────────────────────────
  const handleResend = async () => {
    if (!email || resendState !== 'idle') return;

    setResendState('sending');

    try {
      await authService.requestPasswordReset(email);
      setResendState('sent');

      // Re-enable button after cooldown
      resendCooldownRef.current = setTimeout(() => {
        setResendState('idle');
      }, RESEND_COOLDOWN_MS);
    } catch (err) {
      devLog.error('[WelcomePage] Resend failed:', err);
      // On error: fall back to idle so user can retry
      setResendState('idle');
    }
  };

  // ─── Render helpers ───────────────────────────────────────────────────────

  const renderContent = () => {
    // State 1: No email param — generic fallback
    if (!email) {
      return (
        <div className="text-center">
          <h1 className="text-lg sm:text-[22px] font-bold text-gray-900 mb-4 font-wagner">
            {t('welcome_generic_heading')}
          </h1>
          <p className="text-gray-600 text-sm mb-6">
            {t('welcome_generic_message')}
          </p>
          <Link to="/login">
            <Button
              type="button"
              variant="secondary"
              size="lg"
              className="uppercase tracking-wide w-full sm:w-auto sm:px-12 !h-9 sm:!h-auto !text-xs sm:!text-base"
            >
              {t('welcome_generic_cta')}
            </Button>
          </Link>
        </div>
      );
    }

    // Error from status API — degrade gracefully to generic state
    if (statusError) {
      return (
        <div className="text-center">
          <h1 className="text-lg sm:text-[22px] font-bold text-gray-900 mb-4 font-wagner">
            {t('welcome_generic_heading')}
          </h1>
          <p className="text-gray-600 text-sm mb-6">
            {t('welcome_generic_message')}
          </p>
          <Link to="/login">
            <Button
              type="button"
              variant="secondary"
              size="lg"
              className="uppercase tracking-wide w-full sm:w-auto sm:px-12 !h-9 sm:!h-auto !text-xs sm:!text-base"
            >
              {t('welcome_generic_cta')}
            </Button>
          </Link>
        </div>
      );
    }

    // State 3: Provisioned — show success card (two variants based on needsPasswordSetup)
    if (statusData?.provisioned) {
      // Existing account: already has a password, just needs to log in
      if (!statusData.needsPasswordSetup) {
        return (
          <div className="text-center">
            <h1 className="text-lg sm:text-[22px] font-bold text-gray-900 mb-4 font-wagner">
              {t('welcome_ready_existing_heading')}
            </h1>

            <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
              {t('welcome_ready_existing_message', { email })}
            </div>

            <div className="flex flex-col items-center gap-3">
              <Link to="/login">
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  className="uppercase tracking-wide w-full sm:w-auto sm:px-12 !h-9 sm:!h-auto !text-xs sm:!text-base"
                >
                  {t('welcome_ready_cta')}
                </Button>
              </Link>
            </div>
          </div>
        );
      }

      // New account: needs to set a password via the emailed link
      return (
        <div className="text-center">
          <h1 className="text-lg sm:text-[22px] font-bold text-gray-900 mb-4 font-wagner">
            {t('welcome_ready_heading')}
          </h1>

          <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
            {t('welcome_ready_message', { email })}
          </div>

          <div className="flex flex-col items-center gap-3">
            <Link to="/login">
              <Button
                type="button"
                variant="secondary"
                size="lg"
                className="uppercase tracking-wide w-full sm:w-auto sm:px-12 !h-9 sm:!h-auto !text-xs sm:!text-base"
              >
                {t('welcome_ready_cta')}
              </Button>
            </Link>

            {/* Resend link */}
            {resendState === 'sent' ? (
              <p className="text-xs text-green-600 font-medium">
                {t('welcome_resend_sent')}
              </p>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                disabled={resendState === 'sending'}
                className="text-xs text-gray-400 hover:text-secondary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resendState === 'sending'
                  ? '…'
                  : t('welcome_resend_cta')}
              </button>
            )}
          </div>
        </div>
      );
    }

    // State 2: Polling — workspace being provisioned
    return (
      <div className="text-center">
        <h1 className="text-lg sm:text-[22px] font-bold text-gray-900 mb-4 font-wagner">
          {t('welcome_provisioning_heading')}
        </h1>

        {/* Spinner — matches ResetPassword loading style */}
        <div className="flex justify-center mb-5">
          <svg
            className="animate-spin h-8 w-8 text-secondary-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-label="Loading"
            role="img"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>

        <p className="text-gray-600 text-sm">
          {t('welcome_provisioning_message', { email })}
        </p>
      </div>
    );
  };

  // ─── Page shell (mirrors ResetPassword exactly) ────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center p-4 auth-bg">
      <div className="relative bg-white rounded-lg shadow-2xl px-7 pt-6 pb-3 sm:px-8 sm:pt-8 sm:pb-4 w-full max-w-lg flex flex-col">
        {/* Logo */}
        <div className="flex justify-center mb-4">
          <img
            src="/images/logo.png"
            alt="Ägile Ässets"
            className="w-full max-w-[35%] sm:max-w-[45%] h-auto"
            onError={(e) => {
              devLog.log('Logo failed to load from:', e.currentTarget.src);
              e.currentTarget.onerror = null;
            }}
          />
        </div>

        {/* Tagline — desktop only */}
        <div className="hidden sm:block text-center mb-4 sm:mb-6">
          <p className="text-[11px] uppercase tracking-wider text-gray-900 leading-relaxed font-medium">
            {t('welcome_tagline_creating')}{' '}
            <span className="text-bs-secondary font-bold">{t('welcome_tagline_brand_value')}</span>.
            <br />
            {t('welcome_tagline_one_asset')}
            <br />
            {t('welcome_tagline_content_marketing')}{' '}
            <span className="text-bs-secondary font-bold">{t('welcome_tagline_next_level')}</span>.
          </p>
        </div>

        {/* Dynamic content */}
        <div className="mb-4 sm:mb-8">
          {renderContent()}
        </div>

        {/* Back-to-login footer link */}
        <div className="flex items-center justify-center mb-3">
          <Link
            to="/login"
            className="text-[11px] sm:text-[12px] text-gray-400 hover:text-secondary-500 transition-colors"
          >
            ← {t('auth_forgot_link_back_to_login')}
          </Link>
        </div>

        {/* Footer — matches all auth pages */}
        {/* eslint-disable i18next/no-literal-string */}
        <div className="mt-auto border-t border-gray-200" />
        <p className="pt-3 text-[10px] sm:text-xs text-gray-400 text-center">
          © {new Date().getFullYear()} Made with <span className="text-bs-secondary">❤️</span> by{' '}
          <a
            href="https://overw8.de"
            target="_blank"
            rel="noopener noreferrer"
            className="text-bs-secondary hover:text-secondary-600 font-medium transition-colors"
          >
            OVERW8
          </a>
          <span className="mx-1 text-gray-300">·</span>
          <a
            href="https://agile-assets.ai/house-rules/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 hover:text-secondary-500 transition-colors"
          >
            Terms
          </a>
          <span className="mx-1 text-gray-300">·</span>
          <a
            href="https://agile-assets.ai/packages-pricing/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 hover:text-secondary-500 transition-colors"
          >
            Pricing
          </a>
          <span className="mx-1 text-gray-300">·</span>
          <a
            href="https://agile-assets.ai/about/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 hover:text-secondary-500 transition-colors"
          >
            About
          </a>
        </p>
        {/* eslint-enable i18next/no-literal-string */}
      </div>
    </div>
  );
};
